import { useEffect, useState } from "react";
import { Loader2, UserRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AccountSecurityTab from "@/components/admin/AccountSecurityTab";

const inputClass = "mt-1.5 w-full rounded-lg border border-[#282C37] bg-[#0A0B0E] px-3 py-2 text-amber-50 focus:border-[#D4AF37] focus:outline-none";

export default function SuperAdminAccount() {
  const { checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [initial, setInitial] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/saas/me").then(({ data }) => {
      if (active) { setProfile(data); setInitial(data); }
    }).catch(() => toast.error("Não foi possível carregar sua conta.")).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, []);

  const update = (key, value) => setProfile((old) => ({ ...old, [key]: value }));
  const sensitiveChange = profile && initial && (
    profile.username.trim().toLowerCase() !== initial.username ||
    (profile.recovery_email?.trim().toLowerCase() || null) !== initial.recovery_email
  );

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/saas/me", {
        name: profile.name.trim(),
        username: profile.username.trim(),
        recovery_email: profile.recovery_email?.trim() || null,
        current_password: currentPassword || null,
      });
      setProfile(data);
      setInitial(data);
      setCurrentPassword("");
      await checkAuth?.();
      toast.success("Dados da conta atualizados.");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível salvar os dados.");
    } finally {
      setSaving(false);
    }
  };

  return <div className="max-w-2xl space-y-6 fade-in">
    <p className="text-sm text-stone-400">Atualize seus dados e proteja o acesso à plataforma.</p>
    <form onSubmit={save} className="card-surface p-5 space-y-4">
      <h2 className="flex items-center gap-2 font-sans2 font-semibold text-amber-50"><UserRound className="w-5 h-5 text-[#D4AF37]" /> Dados pessoais</h2>
      {loading ? <p className="text-sm text-stone-400">Carregando sua conta…</p> : profile && <>
        <label className="block text-sm text-stone-300">Nome de exibição<input required maxLength="120" value={profile.name} onChange={(event) => update("name", event.target.value)} className={inputClass} /></label>
        <label className="block text-sm text-stone-300">Usuário/Login<input required minLength="3" maxLength="254" autoComplete="username" value={profile.username} onChange={(event) => update("username", event.target.value)} className={inputClass} /></label>
        <label className="block text-sm text-stone-300">E-mail de recuperação<input type="email" autoComplete="email" value={profile.recovery_email || ""} onChange={(event) => update("recovery_email", event.target.value)} className={inputClass} /></label>
        <p className="text-xs text-stone-500">O e-mail de recuperação não altera seu login.</p>
        <label className="block text-sm text-stone-300">Senha atual {sensitiveChange ? "(obrigatória para alterar login ou e-mail)" : "(para alterações sensíveis)"}<input type="password" autoComplete="current-password" required={Boolean(sensitiveChange)} value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} className={inputClass} /></label>
        <button disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#D4AF37] px-4 py-2.5 text-sm font-semibold text-[#0A0B0E] disabled:opacity-60">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar alterações</button>
      </>}
    </form>
    <AccountSecurityTab embedded />
  </div>;
}
